import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Card,
  CardMedia,
  CardContent,
  useTheme,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { StockCheckSuggestedProducts } from "../../../../../models/Stock/Stock";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import ConfirmationPopup from "../../../../../views/components/common/pop-up/ConfirmationPopup";
import { observer } from "mobx-react";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";

interface SuggestedProductsModalProps {
  open: boolean;
  onClose: () => void;
  products: StockCheckSuggestedProducts[];
  requestId: number;
  productId: number;
  isEditable?: boolean;
  onDelete: (
    requestId: number,
    productId: number,
    suggestedProductId: number,
  ) => void;
  onAdd: () => void;
}

const SuggestedProductsModal: React.FC<SuggestedProductsModalProps> = ({
  open,
  onClose,
  products,
  requestId,
  productId,
  isEditable = false,
  onDelete,
  onAdd,
}) => {
  const theme = useTheme();
  const { stockStore, userStore } = useStores();

  useEffect(() => {
    if (!open) {
      stockStore.setDeleteConfirmSuggestedProductId(null);
    }
  }, [open, stockStore]);

  const handleConfirmDelete = () => {
    if (stockStore.deleteConfirmSuggestedProductId === null) return;
    onDelete(requestId, productId, stockStore.deleteConfirmSuggestedProductId);
    stockStore.setDeleteConfirmSuggestedProductId(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            onClose();
          }
        }}
        fullWidth
        maxWidth="md"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: theme.palette.customColors.backdrop,
              backdropFilter: "blur(4px)",
            },
          },
          paper: {
            sx: {
              borderRadius: "16px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
              maxHeight: "90vh",
              margin: { xs: "16px", sm: "24px" },
              position: "relative",
              background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                pointerEvents: "none",
                zIndex: 1,
                background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
              },
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            mt: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <CustomTypography variant="heading3">
            Suggested Products
          </CustomTypography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 2,
            mt: 2,
            maxHeight: "65vh",
            overflowY: "auto",
          }}
        >
          <Box
            display="flex"
            flexWrap="wrap"
            gap={2}
            justifyContent="flex-start"
          >
            {products.map((suggestedProduct) => (
              <Card
                key={
                  suggestedProduct.suggested_prod_id ??
                  suggestedProduct.id ??
                  suggestedProduct.product_id
                }
                sx={{
                  width: 250,
                  boxShadow: 3,
                  borderRadius: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  position: "relative",
                }}
              >
                {isEditable && (
                  <IconButton
                    aria-label="delete suggested product"
                    onClick={() =>
                      stockStore.setDeleteConfirmSuggestedProductId(
                        suggestedProduct.suggested_prod_id ??
                          suggestedProduct.id!,
                      )
                    }
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      backgroundColor: theme.palette.customColors.pinkLight,
                      width: 30,
                      height: 30,
                      "&:hover": {
                        backgroundColor: theme.palette.customColors.pinkLight,
                      },
                    }}
                  >
                    <DeleteIcon
                      sx={{
                        color: theme.palette.customColors.pinkDark,
                        width: 20,
                        height: 20,
                      }}
                    />
                  </IconButton>
                )}
                {suggestedProduct.product_image_urls &&
                suggestedProduct.product_image_urls.length > 0 ? (
                  <CardMedia
                    component="img"
                    image={suggestedProduct.product_image_urls[0]}
                    alt={suggestedProduct.product_name}
                    sx={{ height: 220, p: 1, pb: 0 }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 150,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: theme.palette.grey[200],
                    }}
                  >
                    <CustomTypography variant="body2">
                      No Image Available
                    </CustomTypography>
                  </Box>
                )}
                <CardContent>
                  <CustomTypography variant="body2" fontWeight={600}>
                    {suggestedProduct.product_name}
                  </CustomTypography>

                  {userStore.hasPermission(
                    [
                      EmployeeRoles.VIEW_COST_PRICE,
                      EmployeeRoles.CHANGE_COST_PRICE,
                    ],
                    true,
                  ) &&
                    suggestedProduct.cost_price != null &&
                    suggestedProduct.cost_price > 0 && (
                      <CustomTypography
                        variant="body2"
                        color={theme.palette.customColors.greyTextColor}
                      >
                        Cost Price: ₹{suggestedProduct.cost_price}
                      </CustomTypography>
                    )}

                  {(suggestedProduct.expected_raw_inward_timeline ||
                    suggestedProduct.timeline) && (
                    <CustomTypography variant="body2">
                      Raw Inward Timeline:{" "}
                      {suggestedProduct.expected_raw_inward_timeline ??
                        suggestedProduct.timeline}{" "}
                      days
                    </CustomTypography>
                  )}
                  {suggestedProduct.expected_print_inward_timeline && (
                    <CustomTypography variant="body2">
                      Print Inward Timeline:{" "}
                      {suggestedProduct.expected_print_inward_timeline} days
                    </CustomTypography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box />
          <Box sx={{ display: "flex", gap: 2 }}>
            <AppButton variant="grayscale" onClick={onClose} label="Cancel" />
            {isEditable && (
              <AppButton
                startIcon={<AddIcon />}
                variant="primary"
                onClick={onAdd}
                label="Add Product"
              />
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <ConfirmationPopup
        id={stockStore.deleteConfirmSuggestedProductId ?? 0}
        open={stockStore.deleteConfirmSuggestedProductId !== null}
        title="Delete Suggested Product"
        content="Are you sure you want to delete this suggested product?"
        onConfirm={handleConfirmDelete}
        onCancel={() => stockStore.setDeleteConfirmSuggestedProductId(null)}
        zIndex={1400}
      />
    </>
  );
};

export default observer(SuggestedProductsModal);
